/**
 * Ambient analysis agent — cross-service contracts.
 *
 * Frozen in phase 0 of `plans/ambient-analysis/`. Consumed by:
 *   - `apps/webhooks` detector + correlator (phases 1-2) — the only
 *     writer of observations and findings.
 *   - `apps/api` staff admin routes (phase 4) — reads findings for the
 *     calibration surface.
 *
 * Everything here describes work done by DETERMINISTIC code. No model
 * computes a metric, decides whether a regression happened, or sets a
 * score — see README guardrail 1. A model may eventually narrate what
 * this taxonomy has already proved, nothing more.
 */
import { z } from 'zod';

// ─── Detectors ─────────────────────────────────────────────────────────────

/**
 * What the detector can notice. One entry per phase-1 detector.
 *
 * Traffic-volume detectors (pageviews, sessions, bounce rate) are
 * deliberately absent: they'd read the columns corrupted by the RUM MV
 * aggregation bug (five `pixel_daily_*` MVs declared
 * `AggregatingMergeTree` with plain `UInt64` instead of
 * `SimpleAggregateFunction(sum, UInt64)`). Adding them before those MVs
 * are re-materialised would calibrate the whole system on wrong data.
 */
export const OBSERVATION_DETECTORS = [
  /** Site/page p75 vs a trailing robust baseline. The primary detector. */
  'rum_cwv_p75',
  /**
   * Share of visits rated "good" for a metric, vs a trailing baseline.
   *
   * Exists because a p75 needs a lot of samples to be trustworthy — it
   * estimates a point in the tail, so at ~40 samples/day it wobbles for
   * reasons that have nothing to do with the site. A PROPORTION over the
   * same 40 samples has a well-defined standard error and behaves
   * sensibly. That is why this detector's sample gate can be far lower
   * than `rum_cwv_p75`'s without lowering the statistical bar: it is a
   * better-conditioned statistic, not a laxer threshold.
   *
   * Measured on real clickweave traffic: the busiest site carries ~10k
   * beacons/30d but only ~1k of them carry an LCP value (most beacons
   * are pageend/visibility/engagement), i.e. ~41 LCP samples/day. Every
   * site we own sits below the p75 gate; most clear this one.
   */
  'rum_good_density',
  /** Per (country, form_factor) p75 — catches "mobile Brazil regressed". */
  'rum_cohort_p75',
  /** Lighthouse lab metrics vs a trailing baseline. */
  'synthetic_cwv',
  /** Third-party entity latest vs its own 30-day average. */
  'third_party_drift',
  /** A third-party entity appeared that wasn't there before. */
  'third_party_new',
  /**
   * Bookkeeping, not a signal: the full third-party roster seen on a
   * visit. Exists so `third_party_new` has a truthful "before" set —
   * stable entities never produce deviation observations, and without
   * a roster every pre-existing script would eventually be flagged
   * "new". A false "new TikTok script" email is precisely the
   * credibility failure this feature cannot afford. The correlator
   * ignores these rows entirely.
   */
  'third_party_seen',
  /** CrUX field data, week-over-week. Mostly a corroboration source. */
  'crux_p75',
  /** Search position / clicks movement. Weakest signal; correlation value. */
  'gsc_position',
] as const;
export type ObservationDetector = (typeof OBSERVATION_DETECTORS)[number];

/**
 * What a finding can be about. Narrower than the detector list —
 * several detectors can feed one kind.
 *
 * Decision order is significant and lives in plan phase 2 §5:
 * cohort divergence is tested BEFORE deploy impact, because a
 * cohort-confined regression that happens to have a deploy nearby is
 * still better described as "your Brazilian mobile users regressed".
 */
export const FINDING_KINDS = [
  'deploy_impact',
  'third_party_impact',
  'cohort_divergence',
  'sustained_shift',
] as const;
export type FindingKind = (typeof FINDING_KINDS)[number];

export const FINDING_DIRECTIONS = ['regression', 'improvement'] as const;
export type FindingDirection = (typeof FINDING_DIRECTIONS)[number];

export const FINDING_STATUSES = ['open', 'resolved', 'dismissed'] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];

// ─── Gates ─────────────────────────────────────────────────────────────────

/**
 * Minimum samples before a scope is eligible at all. Below this we do
 * not write an observation — a p75 over 40 sessions is noise wearing a
 * statistic's clothing.
 *
 * These are FIRST GUESSES. The calibration period (phase 4) exists
 * because they're wrong; expect to raise them.
 */
export const MIN_SAMPLES: Record<ObservationDetector, number> = {
  rum_cwv_p75: 200,
  // A proportion, not a percentile — see the detector's note above. 25
  // samples gives a standard error around 10 percentage points, which
  // the 5-point magnitude floor comfortably clears.
  rum_good_density: 25,
  rum_cohort_p75: 200,
  synthetic_cwv: 5,
  third_party_drift: 3,
  third_party_new: 3,
  third_party_seen: 1,
  // CrUX is pre-aggregated and only published for origins with enough
  // traffic, so presence already implies a sufficient sample.
  crux_p75: 1,
  // Impressions, not sessions.
  gsc_position: 100,
};

/**
 * Robust deviation (in MAD-scaled sigma) at which a detector may
 * produce an observation.
 *
 * Deliberately NOT higher. With ~140 scope×metric combinations tested
 * per site per day, 3σ yields roughly one chance false positive per
 * site per day — but the fix for that is persistence + corroboration
 * (phase 2), not a bigger sigma. Real regressions are often only 3-4σ
 * against a noisy baseline, so raising this trades a noise problem for
 * a blindness problem.
 */
export const DEVIATION_THRESHOLD = 3.0;

/**
 * Per-metric floor on the MAD-derived sigma. A perfectly flat series —
 * common on low-traffic sites where p75 lands in the same bucket every
 * day — otherwise yields sigma 0 and an infinite deviation.
 *
 * Values are the smallest movement considered physically meaningful.
 */
export const SIGMA_FLOORS: Record<string, number> = {
  lcp: 25,
  fcp: 25,
  ttfb: 25,
  inp: 10,
  cls: 0.005,
  performance_score: 1.5,
  // Densities are fractions in 0-1. At ~25-40 samples a day the
  // sampling noise alone is several points, so the floor has to sit
  // above it or every ordinary day reads as a deviation.
  lcp_good_density: 0.03,
  inp_good_density: 0.03,
  cls_good_density: 0.03,
  ttfb_good_density: 0.03,
  fcp_good_density: 0.03,
};

/**
 * Trailing baseline length for the RUM detectors, in days.
 *
 * 21 rather than 28 so a site becomes analysable three weeks after its
 * pixel goes in rather than four — and NOT shorter, because the
 * same-weekday sub-baseline needs at least 3 matching weekdays to stay
 * active. At 14 days it would silently fall back to 2 and disable the
 * seasonality defence, which is how a system starts reporting every
 * Monday as a regression.
 */
export const RUM_BASELINE_DAYS = 21;

/**
 * Absolute-change floor. A metronomically stable site can produce a 6σ
 * move that is 8ms — statistically real, practically irrelevant. Both
 * this and DEVIATION_THRESHOLD must be cleared.
 */
export const MAGNITUDE_FLOORS: Record<string, number> = {
  lcp: 100,
  fcp: 100,
  ttfb: 100,
  inp: 40,
  cls: 0.02,
  performance_score: 4,
  // 5 percentage points of "good" share. Below this it isn't worth a
  // customer's attention even when the statistics are certain.
  lcp_good_density: 0.05,
  inp_good_density: 0.05,
  cls_good_density: 0.05,
  ttfb_good_density: 0.05,
  fcp_good_density: 0.05,
};

/**
 * Direction semantics. `true` = a higher value is worse.
 *
 * Encoded once, here, rather than in each detector — getting it wrong
 * reports improvements as regressions, which is the most embarrassing
 * failure this system has available. Note `position`: GSC reports a
 * rank where 1 is best, so higher is worse.
 */
export const HIGHER_IS_WORSE: Record<string, boolean> = {
  lcp: true,
  cls: true,
  inp: true,
  ttfb: true,
  fcp: true,
  main_thread_ms: true,
  transfer_kb: true,
  position: true,
  performance_score: false,
  clicks: false,
  impressions: false,
  // A rising share of "good" experiences is an improvement. Getting
  // these inverted would report every recovery as a regression, which
  // is the most embarrassing failure this system has available.
  lcp_good_density: false,
  inp_good_density: false,
  cls_good_density: false,
  ttfb_good_density: false,
  fcp_good_density: false,
};

/**
 * Consecutive deviating days required before a finding may be created.
 * Independent daily false positives essentially never repeat on the
 * same scope+metric; real regressions always do. This is the primary
 * defence against the multiple-comparisons problem.
 */
export const MIN_PERSISTENCE_DAYS = 2;

/** Findings are created at or above this proof score. */
export const FINDING_PROOF_THRESHOLD = 55;

/** Findings at or above this are marked high-confidence. */
export const FINDING_HIGH_CONFIDENCE_THRESHOLD = 75;

/**
 * Consecutive days with no deviating observation before an open
 * finding is auto-resolved. This is what keeps the catalogue relevant
 * without a human curating it.
 */
export const FINDING_AUTO_RESOLVE_DAYS = 3;

// ─── Evidence ──────────────────────────────────────────────────────────────

/**
 * One observation as embedded in a finding's evidence bundle. Denormalised
 * from `site_observations` so a finding is readable without a join and
 * stays truthful even if the observation is later pruned.
 */
export const EvidenceObservationSchema = z.object({
  detector: z.string(),
  scope: z.string(),
  metric: z.string(),
  observed_on: z.string(),
  value: z.number(),
  baseline: z.number(),
  deviation: z.number(),
  sample_count: z.number(),
  change_abs: z.number(),
  change_pct: z.number(),
});

/**
 * A candidate cause. We are not proving causation — we are recording
 * what could explain the movement, and how well it lines up.
 */
export const EvidenceCandidateSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('deploy'),
    deploy_id: z.string(),
    deployed_at: z.string(),
    /**
     * What actually shipped, when the marker knows. GitHub-bridged
     * markers carry all four; CD-webhook markers carry whatever the
     * customer's pipeline sent. Copy rule: with a sha present, name it
     * — "the deploy of a1b2c3d on main" — and NEVER fall back to
     * "whatever shipped that day", which reads as a shrug. Without a
     * sha, say "a deploy on <date>" and stop there.
     */
    commit_sha: z.string().nullable().optional(),
    branch: z.string().nullable().optional(),
    deployer: z.string().nullable().optional(),
    deploy_url: z.string().nullable().optional(),
    /** Hours between the deploy and the first deviating day. */
    alignment_hours: z.number(),
    /**
     * Deploys on this site in the trailing 14 days — the coincidence
     * prior. On a site shipping 3x/day, "a deploy happened near this"
     * is nearly information-free, and the score penalises accordingly
     * (plan phase 2 §3.2).
     */
    deploy_frequency_14d: z.number(),
    /** Pre/post windows around this specific marker, from site_deploy_delta. */
    delta: z.record(z.string(), z.number().nullable()).optional(),
  }),
  z.object({
    type: z.literal('third_party_new'),
    entity: z.string(),
    first_seen_on: z.string(),
    main_thread_ms: z.number().nullable(),
    pages_seen: z.number(),
    total_pages: z.number(),
  }),
  z.object({
    type: z.literal('third_party_drift'),
    entity: z.string(),
    latest_main_thread_ms: z.number().nullable(),
    avg30_main_thread_ms: z.number().nullable(),
    change_pct: z.number(),
  }),
  z.object({
    type: z.literal('traffic_mix'),
    dimension: z.string(),
    bucket: z.string(),
    share_before: z.number(),
    share_after: z.number(),
  }),
]);

export const EVIDENCE_CORROBORATION_SOURCES = [
  'crux',
  'synthetic',
  'rum',
  'gsc',
  'conversions',
] as const;

/**
 * The full evidence bundle stored on `site_findings.evidence`.
 *
 * An empty `candidates` array is valid and meaningful: a real,
 * corroborated shift with no identifiable cause is still worth
 * surfacing (that's `sustained_shift`).
 */
export const EvidenceSchema = z.object({
  observations: z.array(EvidenceObservationSchema).min(1),
  /** Consecutive days the deviation held. */
  persistence_days: z.number(),
  candidates: z.array(EvidenceCandidateSchema),
  /** Independent datasets that agree the movement is real. */
  corroboration: z.array(
    z.object({
      source: z.enum(EVIDENCE_CORROBORATION_SOURCES),
      metric: z.string(),
      agrees: z.boolean(),
      detail: z.string(),
    }),
  ),
  /** Reach. Drives priority, not visibility. */
  impact: z.object({
    sessions_affected: z.number(),
    share_of_traffic: z.number(),
    conversion_change_pct: z.number().nullable(),
  }),
  /** Component breakdown of the proof score, for the calibration surface. */
  score_components: z.record(z.string(), z.number()).optional(),
});

export type EvidenceObservation = z.infer<typeof EvidenceObservationSchema>;
export type EvidenceCandidate = z.infer<typeof EvidenceCandidateSchema>;
export type Evidence = z.infer<typeof EvidenceSchema>;

/**
 * Resolve whether a movement is a regression or an improvement.
 * Unknown metrics default to "higher is worse", which is true of every
 * timing metric we hold — but a metric missing from HIGHER_IS_WORSE is
 * a bug, not a case to handle gracefully.
 */
export function findingDirection(metric: string, deviation: number): FindingDirection {
  const higherIsWorse = HIGHER_IS_WORSE[metric] ?? true;
  return deviation > 0 === higherIsWorse ? 'regression' : 'improvement';
}
